import { FC, PropsWithChildren } from "react"
import { SmallScreenErrorView } from "./error-views/SmallScreenErrorView"
import { WebGLUnavailableErrorView } from "./error-views/WebGLUnavailableErrorView"

import { getGPUDebugInfo } from "@/utils/gpu"
import { setGlobalSentryContext } from "@/telemetry/sentry-peers"
import { analytics } from "@/telemetry/amplitude"
import { useOldChromeWarning } from "@/utils/chrome-version"
import { currentBrowserName, currentBrowserVersion } from "@/utils/browser"
import { useDetectBrowserCapabilities } from "@/store/app/browser-capabilities"
import { browserCompatibility } from "@/utils/browser-compatibility"

export type RequiredBrowserAPI = "webgl"

export class UnsupportedBrowserError extends Error {}
export class SmallScreenError extends Error {}
export class BrowserAPIAvailabilityError extends Error {
	api: RequiredBrowserAPI

	constructor(api: RequiredBrowserAPI) {
		super()
		this.api = api
	}
}

try {
	const gpuDebugInfo = getGPUDebugInfo()
	setGlobalSentryContext("browserCompatibility", browserCompatibility)
	setGlobalSentryContext("GPU", gpuDebugInfo)
	analytics.trackEvent({
		type: "system",
		label: "gpu_renderer",
		value: gpuDebugInfo.renderer,
	})
} catch (error) {
	console.error("[BrowserCompatibilityGuard] module init failed:", error)
}

export const BrowserCompatibilityGuard: FC<PropsWithChildren> = ({ children }) => {
	useDetectBrowserCapabilities()

	useOldChromeWarning({
		browserName: currentBrowserName,
		browserVersion: currentBrowserVersion,
	})

	if (!browserCompatibility.screen) {
		return <SmallScreenErrorView />
	}
	if (!browserCompatibility.webGL) {
		return <WebGLUnavailableErrorView />
	}
	if (!browserCompatibility.webGL2) {
		return <WebGLUnavailableErrorView api="webgl2" />
	}

	return <>{children}</>
}
